import React, { ReactNode } from 'react'
import { DynamicLayout, Separator, YStack } from '@red-ui/components'
import { ContentfulPageContent, contentTypeOf } from '@vrw/data/src/utils/contentfulPages'
import { DynamicContentFactory } from '..'
import { Tabs } from './Tabs'

interface TabbedContentBlockFields {
  backgroundColour?: string
  tabsHeader?: ContentfulPageContent
  tabs: {
    fields: {
      title: string
      sections: ContentfulPageContent[]
    }
  }[]
}

const MAX_SECTION_WIDTH = 1320

const TabsHeader = ({ content, backgroundColour }: { content: ContentfulPageContent; backgroundColour?: string }) => (
  <YStack
    alignItems="center"
    backgroundColor={backgroundColour}
    paddingTop="$2"
    paddingHorizontal="$2"
    $gtMobile={{ paddingTop: '$4', paddingHorizontal: '$4' }}
    $gtTabletLandscape={{ paddingHorizontal: '$8' }}>
    <YStack maxWidth={MAX_SECTION_WIDTH} width="100%">
      <DynamicContentFactory content={content} />
    </YStack>
  </YStack>
)

const SeparatorComponent = ({ backgroundColour }: { backgroundColour?: string }) => (
  <YStack
    alignItems="center"
    backgroundColor={backgroundColour}
    paddingHorizontal="$2"
    $gtMobile={{ paddingHorizontal: '$4' }}
    $gtTabletLandscape={{ paddingHorizontal: '$8' }}>
    <Separator maxWidth={MAX_SECTION_WIDTH} width="100%" borderColor="$white" borderWidth={1} />
  </YStack>
)

const ListWrapper = ({ children, backgroundColour }: { children: ReactNode; backgroundColour?: string }) => (
  <>
    <YStack
      alignItems="center"
      backgroundColor={backgroundColour}
      padding="$2"
      $gtMobile={{ padding: '$4' }}
      $gtTabletLandscape={{ paddingHorizontal: '$8' }}>
      <YStack
        borderWidth="$0.25"
        borderColor="$borderColor"
        justifyContent="center"
        alignItems="center"
        backgroundColor="$background"
        padding="$0.25"
        borderRadius={500}
        maxWidth={MAX_SECTION_WIDTH}>
        {children}
      </YStack>
    </YStack>
    <SeparatorComponent backgroundColour={backgroundColour} />
  </>
)

export const TabbedContentBlock = ({ fields }: { fields: TabbedContentBlockFields }) => {
  const { backgroundColour, tabsHeader, tabs } = fields ?? {}
  return (
    <YStack>
      {tabsHeader && <TabsHeader content={tabsHeader} backgroundColour={backgroundColour} />}

      <Tabs
        defaultValue={tabs[0]?.fields.title}
        size="$4"
        width="100%" // needed for full width otherwise it will be the width of the tabs and content
        renderListWrapper={(children) => <ListWrapper backgroundColour={backgroundColour}>{children}</ListWrapper>}>
        <Tabs.List>
          {tabs.map(({ fields: { title } }) => (
            // id included for tracking purposes
            <Tabs.Tab key={title} value={title} id={title.replaceAll(' ', '-')}>
              <Tabs.Text>{title}</Tabs.Text>
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabs.map(({ fields: { title, sections } }) => (
          <Tabs.Content key={title} value={title} animated>
            {sections.map((section) => {
              const sectionType = contentTypeOf(section)
              return sectionType === 'dynamicLayout' ? (
                <DynamicLayout key={section.sys.id} fields={section.fields} isSection>
                  {section.fields.content?.map((x: ContentfulPageContent) => <DynamicContentFactory key={x.sys.id} content={x} />)}
                </DynamicLayout>
              ) : (
                <DynamicContentFactory key={section.sys.id} content={section} />
              )
            })}
          </Tabs.Content>
        ))}
      </Tabs>
    </YStack>
  )
}
